import { useState, useEffect } from "react";
import { Target, TrendingUp, Shield, Users, Handshake, CheckCircle2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { AccountStatusBadge, BuyingModeBadge } from "@/components/targeting/StatusBadge";
import { useUpdateAccount } from "@/hooks/useTargetUniverse";

interface PipelinePrepTabProps {
  company: Record<string, any>;
}

export function PipelinePrepTab({ company }: PipelinePrepTabProps) {
  const updateAccount = useUpdateAccount();

  // Pipeline prep fields
  const [buyingMode, setBuyingMode] = useState<string>("");
  const [budgetConfirmed, setBudgetConfirmed] = useState(false);
  const [fitConfirmed, setFitConfirmed] = useState(false);
  const [warmIntro, setWarmIntro] = useState(false);
  const [champion, setChampion] = useState(false);
  // Account status
  const [tier, setTier] = useState<string>("unreviewed");

  useEffect(() => {
    if (company) {
      setBuyingMode(company.buying_mode || "");
      setBudgetConfirmed(company.budget_confirmed || false);
      setFitConfirmed(company.fit_confirmed || false);
      setWarmIntro(company.warm_intro_available || false);
      setChampion(company.champion_identified || false);
      setTier(company.account_tier || "unreviewed");
    }
  }, [company]);

  const pipelineGateMet =
    buyingMode && ["growth", "trouble", "overconfident"].includes(buyingMode) &&
    budgetConfirmed && fitConfirmed && warmIntro && champion;

  const criteriaCount = [budgetConfirmed, fitConfirmed, warmIntro, champion].filter(Boolean).length;

  const handleSave = async () => {
    const oldTier = company.account_tier || "unreviewed";

    try {
      await updateAccount.mutateAsync({
        accountId: company.id,
        fields: {
          buying_mode: buyingMode || null,
          budget_confirmed: budgetConfirmed,
          fit_confirmed: fitConfirmed,
          warm_intro_available: warmIntro,
          champion_identified: champion,
          account_tier: tier,
          last_reviewed_at: new Date().toISOString(),
          last_reviewed_by: "User",
          ...(oldTier !== tier ? { tier_changed_at: new Date().toISOString(), tier_changed_by: "User" } : {}),
        },
        activityType: oldTier !== tier ? "tier_changed" : "reviewed",
        activityDescription:
          oldTier !== tier
            ? `Tier changed from ${oldTier} to ${tier}`
            : "Pipeline prep updated",
        activityMetadata: {
          old_tier: oldTier,
          new_tier: tier,
          buying_mode: buyingMode,
          budget_confirmed: budgetConfirmed,
          fit_confirmed: fitConfirmed,
          warm_intro: warmIntro,
          champion: champion,
        },
      });
      toast.success("Pipeline prep updated", { description: `${company.company_name} saved successfully.` });
    } catch (err) {
      toast.error("Failed to update pipeline prep");
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* ICP & Tier Summary */}
      <div className="flex items-center gap-3 flex-wrap">
        <AccountStatusBadge status={company.account_tier} />
        {company.buying_mode && <BuyingModeBadge mode={company.buying_mode} />}
        {company.icp_tier && (
          <Badge variant="outline" className="text-xs">
            <Target className="h-3 w-3 mr-1" />
            {company.icp_tier}{company.icp_score ? ` (${company.icp_score})` : ""}
          </Badge>
        )}
        <Badge variant={pipelineGateMet ? "default" : "secondary"} className="text-xs">
          {pipelineGateMet ? "Pipeline Gate Met" : `${criteriaCount}/4 Criteria Met`}
        </Badge>
      </div>

      {/* Pipeline Gate Banner */}
      {pipelineGateMet && (
        <div className="rounded-lg bg-green-50 border border-green-200 p-4 flex items-center gap-3">
          <CheckCircle2 className="h-6 w-6 text-green-600 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-green-700">Pipeline Gate Met</p>
            <p className="text-xs text-green-600">All qualification criteria are satisfied — this account is ready for pipeline entry.</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pipeline Checklist Card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-primary" />
              Pipeline Qualification Checklist
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Check off criteria as you qualify this account for pipeline entry.
            </p>
          </CardHeader>
          <CardContent className="space-y-5">
            <div>
              <Label className="text-xs font-medium">Buying Mode</Label>
              <Select value={buyingMode} onValueChange={setBuyingMode}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Set buying mode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="growth">Growth</SelectItem>
                  <SelectItem value="trouble">Trouble</SelectItem>
                  <SelectItem value="even_keel">Even Keel</SelectItem>
                  <SelectItem value="overconfident">Overconfident</SelectItem>
                  <SelectItem value="unknown">Unknown</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground mt-1">
                Growth, Trouble, or Overconfident indicate active buying signals.
              </p>
            </div>

            <Separator />

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-muted-foreground" />
                  <Label htmlFor="pp-budget" className="text-sm cursor-pointer">Budget Confirmed</Label>
                </div>
                <Switch checked={budgetConfirmed} onCheckedChange={setBudgetConfirmed} id="pp-budget" />
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Target className="h-4 w-4 text-muted-foreground" />
                  <Label htmlFor="pp-fit" className="text-sm cursor-pointer">Fit Confirmed</Label>
                </div>
                <Switch checked={fitConfirmed} onCheckedChange={setFitConfirmed} id="pp-fit" />
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Handshake className="h-4 w-4 text-muted-foreground" />
                  <Label htmlFor="pp-warm" className="text-sm cursor-pointer">Warm Intro Available</Label>
                </div>
                <Switch checked={warmIntro} onCheckedChange={setWarmIntro} id="pp-warm" />
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <Label htmlFor="pp-champion" className="text-sm cursor-pointer">Champion Identified</Label>
                </div>
                <Switch checked={champion} onCheckedChange={setChampion} id="pp-champion" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Account Status Card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Shield className="h-4 w-4 text-primary" />
              Account Status
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Set the operating status for this account.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <Select value={tier} onValueChange={setTier}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unreviewed">Unreviewed</SelectItem>
                <SelectItem value="cold">Cold</SelectItem>
                <SelectItem value="validated_target">Validated Target</SelectItem>
                <SelectItem value="nurture">Nurture</SelectItem>
                <SelectItem value="pipeline_ready">Pipeline Ready</SelectItem>
                <SelectItem value="customer">Customer</SelectItem>
                <SelectItem value="disqualified">Disqualified</SelectItem>
                <SelectItem value="parked">Parked</SelectItem>
              </SelectContent>
            </Select>

            <div className="rounded-md bg-muted/50 p-3 space-y-2 text-xs text-muted-foreground">
              <p><strong>Unreviewed</strong> — Not yet looked at</p>
              <p><strong>Cold</strong> — Reviewed, not yet warm</p>
              <p><strong>Validated Target</strong> — Reviewed and validated as a real target</p>
              <p><strong>Nurture</strong> — Worth staying in touch</p>
              <p><strong>Pipeline Ready</strong> — Gate conditions met</p>
              <p><strong>Customer</strong> — Using CARR products</p>
              <p><strong>Disqualified</strong> — Not a fit, document why</p>
              <p><strong>Parked</strong> — Temporarily shelved, revisit later</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Save */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={updateAccount.isPending}>
          {updateAccount.isPending ? "Saving..." : "Save Pipeline Prep"}
        </Button>
      </div>
    </div>
  );
}
